import React, { useState, useEffect } from 'react';
import { useAuth } from '@/App';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Skeleton } from '@/components/ui/skeleton';
import { toast } from 'sonner';
import { 
  Users, Activity, Clock, Bell, RefreshCw, 
  FileEdit, FilePlus, Trash2, Eye, Download, Circle
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ActionIcon = ({ action }) => {
  const iconMap = {
    created: <FilePlus className="h-4 w-4 text-green-500" />,
    updated: <FileEdit className="h-4 w-4 text-blue-500" />,
    deleted: <Trash2 className="h-4 w-4 text-red-500" />,
    viewed: <Eye className="h-4 w-4 text-gray-500" />,
    exported: <Download className="h-4 w-4 text-purple-500" />
  };
  return iconMap[action] || <Activity className="h-4 w-4" />;
};

const StatusIndicator = ({ status }) => {
  const colorMap = {
    online: 'bg-green-500',
    away: 'bg-yellow-500',
    busy: 'bg-red-500',
    offline: 'bg-gray-400'
  };
  return (
    <span className={`absolute bottom-0 right-0 h-3 w-3 rounded-full border-2 border-background ${colorMap[status] || 'bg-gray-400'}`} />
  );
};

export default function Collaboration() {
  const { token, user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [onlineUsers, setOnlineUsers] = useState([]);
  const [activityFeed, setActivityFeed] = useState([]);
  const [recentChanges, setRecentChanges] = useState([]);

  const fetchData = async () => {
    try {
      // Fetch online users
      const usersRes = await fetch(`${API_URL}/api/presence/online`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (usersRes.ok) {
        const data = await usersRes.json();
        setOnlineUsers(data.online_users || []);
      }

      // Fetch activity feed
      const activityRes = await fetch(`${API_URL}/api/activity/feed?limit=30`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (activityRes.ok) {
        const data = await activityRes.json();
        setActivityFeed(data.activities || []);
      }

      // Fetch recent changes
      const recentRes = await fetch(`${API_URL}/api/activity/recent?minutes=60`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (recentRes.ok) {
        const data = await recentRes.json();
        setRecentChanges(data.recent_changes || []);
      }
    } catch (error) {
      console.error('Failed to fetch collaboration data:', error);
    } finally {
      setLoading(false);
    }
  };

  const updatePresence = async () => {
    try {
      await fetch(`${API_URL}/api/presence/update?page=${window.location.pathname}&status=online`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` }
      });
    } catch (error) {
      console.error('Failed to update presence');
    }
  };

  useEffect(() => {
    fetchData();
    updatePresence();
    
    // Set up polling for real-time updates
    const interval = setInterval(() => {
      fetchData();
      updatePresence();
    }, 30000); // Every 30 seconds

    return () => clearInterval(interval);
  }, [token]);

  const getInitials = (name) => {
    if (!name) return '?';
    return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
  };

  const formatTime = (timestamp) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diff = (now - date) / 1000;

    if (diff < 60) return 'Just now';
    if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
    return date.toLocaleDateString();
  };

  if (loading) {
    return (
      <div className="space-y-6" data-testid="collaboration-loading">
        <Skeleton className="h-10 w-64" />
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <Skeleton className="h-64" />
          <Skeleton className="h-64 lg:col-span-2" />
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="collaboration-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight flex items-center gap-3">
            <Users className="h-8 w-8 text-primary" />
            Team Activity
          </h1>
          <p className="text-muted-foreground mt-1">See who's online and track recent changes</p>
        </div>
        <Button variant="outline" onClick={fetchData} data-testid="refresh-activity">
          <RefreshCw className="h-4 w-4 mr-2" />
          Refresh
        </Button>
      </div>

      {/* Notifications Banner */}
      {recentChanges.length > 0 && (
        <Card className="border-blue-500/50 bg-blue-500/5">
          <CardContent className="py-3">
            <div className="flex items-center gap-3">
              <Bell className="h-5 w-5 text-blue-500" />
              <p className="text-sm">
                <span className="font-medium">{recentChanges.length} recent changes</span> by team members in the last hour
              </p>
            </div>
          </CardContent>
        </Card>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Online Users */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center gap-2">
              <Circle className="h-3 w-3 fill-green-500 text-green-500" />
              Online Now
            </CardTitle>
            <CardDescription>{onlineUsers.length} team member(s)</CardDescription>
          </CardHeader>
          <CardContent>
            {onlineUsers.length === 0 ? (
              <p className="text-sm text-muted-foreground text-center py-8">
                No team members online
              </p>
            ) : (
              <div className="space-y-3">
                {onlineUsers.map((u, idx) => (
                  <div key={idx} className="flex items-center gap-3 p-2 rounded-lg hover:bg-muted">
                    <div className="relative">
                      <Avatar className="h-10 w-10">
                        <AvatarFallback className="bg-primary/10 text-primary">
                          {getInitials(u.user_name)}
                        </AvatarFallback>
                      </Avatar>
                      <StatusIndicator status={u.status} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-sm truncate">{u.user_name}</p>
                      <p className="text-xs text-muted-foreground truncate">
                        {u.current_page ? `Viewing ${u.current_page}` : 'Active'}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Activity Feed */}
        <Card className="lg:col-span-2">
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center gap-2">
              <Activity className="h-5 w-5" />
              Activity Feed
            </CardTitle>
            <CardDescription>Recent actions by team members</CardDescription>
          </CardHeader>
          <CardContent>
            {activityFeed.length === 0 ? (
              <p className="text-sm text-muted-foreground text-center py-8">
                No recent activity
              </p>
            ) : (
              <ScrollArea className="h-[400px] pr-4">
                <div className="space-y-4">
                  {activityFeed.map((activity, idx) => (
                    <div key={idx} className="flex items-start gap-3 pb-4 border-b last:border-0">
                      <div className="relative">
                        <Avatar className="h-8 w-8">
                          <AvatarFallback className="bg-primary/10 text-primary text-xs">
                            {getInitials(activity.user_name)}
                          </AvatarFallback>
                        </Avatar>
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <ActionIcon action={activity.action} />
                          <span className="font-medium text-sm">{activity.user_name}</span>
                          <span className="text-sm text-muted-foreground">
                            {activity.action} {activity.resource_type}
                          </span>
                        </div>
                        {activity.resource_name && (
                          <p className="text-sm text-muted-foreground truncate">
                            {activity.resource_name}
                          </p>
                        )}
                        <div className="flex items-center gap-2 mt-1">
                          <Clock className="h-3 w-3 text-muted-foreground" />
                          <span className="text-xs text-muted-foreground">
                            {formatTime(activity.timestamp)}
                          </span>
                          <Badge variant="outline" className="text-xs">
                            {activity.resource_type}
                          </Badge>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </ScrollArea>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
